/**
 * Methodology overlay dispatcher.
 *
 * Maps a `MethodologyOverlay` discriminated union to the correct pure
 * draw function. Adding a new methodology = add a new case here + a new
 * file in this folder; nothing else in `Chart.tsx` changes.
 */
import type {
  MethodologyOverlay,
  AnalysisZone,
  FairValueGap,
  LiquidityPool,
} from '@trading/shared';
import type { OverlayDrawContext } from './types';
import { drawSMCOverlay } from './SMCOverlay';
import { drawICTOverlay } from './ICTOverlay';
import { drawPriceActionOverlay } from './PriceActionOverlay';
import { drawFibonacciOverlay } from './FibonacciOverlay';

export type { OverlayDrawContext } from './types';
export { drawSMCOverlay, drawICTOverlay, drawPriceActionOverlay, drawFibonacciOverlay };

export function drawMethodologyOverlay(
  overlay: MethodologyOverlay,
  ctx: OverlayDrawContext,
): void {
  switch (overlay.kind) {
    case 'smc':
      drawSMCOverlay(overlay, ctx);
      return;
    case 'ict':
      drawICTOverlay(overlay, ctx);
      return;
    case 'price-action':
      drawPriceActionOverlay(overlay, ctx);
      return;
    case 'fibonacci':
      drawFibonacciOverlay(overlay, ctx);
      return;
  }
}

/**
 * Builds an `SMCOverlay` from the legacy `zones`/`structure` shape so the new
 * dispatcher can render old analyses uniformly. Returns `null` when there is
 * nothing to draw (e.g. validation-only history rows).
 */
export function legacyToSMCOverlay(
  legacy:
    | {
        zones?: { orderBlocks?: unknown[]; fvg?: unknown[]; liquidity?: unknown[] };
        structure?: {
          lastBos?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
          lastChoch?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
          keyLevels?: { recentHigh: number; recentLow: number; swingHigh: number; swingLow: number };
        };
      }
    | null
    | undefined,
): MethodologyOverlay | null {
  if (!legacy) return null;
  const orderBlocks = (legacy.zones?.orderBlocks ?? []) as AnalysisZone[];
  const fvg = (legacy.zones?.fvg ?? []) as FairValueGap[];
  const liquidity = (legacy.zones?.liquidity ?? []) as LiquidityPool[];

  // Skip allocating an empty overlay — keeps the chart pristine for failed/
  // pending analyses that never produced zones.
  const hasContent =
    orderBlocks.length > 0 ||
    fvg.length > 0 ||
    liquidity.length > 0 ||
    !!legacy.structure?.lastBos ||
    !!legacy.structure?.lastChoch ||
    !!legacy.structure?.keyLevels;
  if (!hasContent) return null;

  return {
    kind: 'smc',
    orderBlocks,
    fvg,
    liquidity,
    bos: legacy.structure?.lastBos ?? null,
    choch: legacy.structure?.lastChoch ?? null,
    keyLevels: legacy.structure?.keyLevels,
  };
}
